import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { SearchService } from '@client/app/form-field-dynamic/search.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { AbstractControlValueAccessor } from '@client/app/form-field-dynamic/abstract-control-value-accessor';

export type MonDynamicComponentRightItemLabelType = {
    name: string;
    class?: string;
    tooltipText?: string;
};

@Component({
    selector: 'form-field-dynamic',
    templateUrl: './form-field-dynamic.component.html',
    styleUrls: ['./form-field-dynamic.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: FormFieldDynamicComponent,
        multi: true,
    }],
    standalone: false,
})
export class FormFieldDynamicComponent extends AbstractControlValueAccessor<any[]> implements OnInit {
    @Input()
    set model (value: any[]) {
        this.value = value;
        if (value && value.length === 0) {
            this.pushToModel();
        } else {
            this.updateModels();
        }
    }
    get model (): any[] {
        return this.value;
    }

    @Input()
    allowSearch: boolean = false;

    @Input()
    allowPagination: boolean = false;

    @Input()
    pageSize: number = 999;

    // Label name for the input fields
    @Input()
    label: string = '';

    // Input type..
    @Input()
    type: string = 'text';

    @Input()
    placeholder: string = '';

    @Input()
    confirmMsg: string = this.translateService.getString('Are you sure you want to remove this?');

    // Property name on the model collection
    @Input()
    attrName: string = 'name';

    @Input()
    helpText: string = '';

    @Input()
    textInAddBtn: string = '';

    @Input()
    monRightItemLabelsTooltip: string = '';
    @Input()
    monRightItemLabelsClass: string = 'label label-info';
    @Input()
    monLabelDeleteButtonClass?: string = 'mon-dynamic-label-delete-button';
    @Input()
    monLabelDeleteButtonLabel: string = this.translateService.getString('Press to remove the label.');

    @Input()
    set monDynamicComponentRightItemLabels (value: Array<MonDynamicComponentRightItemLabelType | undefined>) {
        this._monDynamicComponentRightItemLabels = value;
        this.updateRightLabels();
    }
    get monDynamicComponentRightItemLabels (): Array<MonDynamicComponentRightItemLabelType | undefined> | undefined {
        return this._monDynamicComponentRightItemLabels;
    }

    @Output()
    monLabelDeleteButtonClick: EventEmitter<number> = new EventEmitter();
    @Output()
    inputValueChanged: EventEmitter<{ index: number; value: string }> = new EventEmitter();

    page: number = 1;
    search: string = '';
    models: any[] = [];
    labelText: string = '';
    rightItemLabelsPerPage?: Array<MonDynamicComponentRightItemLabelType | undefined>;

    private _monDynamicComponentRightItemLabels?: Array<MonDynamicComponentRightItemLabelType | undefined>;

    constructor (
        private searchService: SearchService,
        private translateService: TranslateService,
    ) {
        super();
    }

    ngOnInit (): void {
        this.labelText = this.getLabelForId(this.label) || 'formFieldDynamic';
    }

    onSearchChanged (search: string): void {
        this.search = search;
        this.updateModels();
        if (search.length) {
            this.page = 1;
        }
    }

    pushToModel (): void {
        let valueLength = 0;
        if (Array.isArray(this.value)) {
            this.value.push({
                [this.attrName]: '',
            });
            valueLength = this.value.length;
        }
        if (Array.isArray(this._monDynamicComponentRightItemLabels)) {
            this._monDynamicComponentRightItemLabels.push(undefined);
            this.updateRightLabels();
        }
        const newPage = Math.ceil(valueLength / this.pageSize) || 1;
        if (newPage !== this.page) {
            this.page = newPage;
            this.onPageParamsChange();
        }
        this.updateModels();
    }

    removeFromModel (model: any): void {
        // NB! May be necessary to set the indexOf search in a private function for unifying search for model reference on input change.
        if (Array.isArray(this.value)) {
            const index = this.value.indexOf(model);
            if (index !== -1) {
                this.value.splice(index, 1);
                if (this.value.length <= this.pageSize) {
                    if (this.page !== 1) {
                        this.page = 1;
                        this.onPageParamsChange();
                    }
                }
                if (Array.isArray(this._monDynamicComponentRightItemLabels)) {
                    this._monDynamicComponentRightItemLabels.splice((this.page - 1) * this.pageSize + index, 1);
                    this.updateRightLabels();
                }
            }
        }
    }

    onMonLabelDeleteButtonClick (index: number): void {
        this.monLabelDeleteButtonClick.emit(index);
    }

    /*
        It is crucial, that in the template this method is located before ngModel.
        Otherwise it does not get the previous value.
        It must be
        (ngModelChange)="inputChangeRegisterBeforeModelChange(indexPosition, $event, model[attrName])"
        [(ngModel)]="model[attrName]"
    */
    inputChangeRegisterBeforeModelChange (currentIndex: number, value: string, previousValue: string): void {

        if (value !== previousValue) {
            let index = currentIndex;

            if (this.allowPagination) {
                index = (this.page - 1) * this.pageSize + currentIndex;
            }
            this.inputValueChanged.emit({
                index,
                value,
            });
        }
    }

    onPageParamsChange (): void {
        this.updateRightLabels();
    }

    private updateRightLabels (): void {
        if (Array.isArray(this._monDynamicComponentRightItemLabels)) {
            this.rightItemLabelsPerPage = this._monDynamicComponentRightItemLabels.slice(
                (this.page - 1) * this.pageSize, this.page * this.pageSize,
            );
        }
    }

    private updateModels (): void {
        if (Array.isArray(this.value)) {
            if (!this.search) {
                this.models = this.value;
                return;
            }
            this.models = this.value.filter((model) => {
                return !model[this.attrName] || this.searchService.fuzzy(this.search, model[this.attrName] as string);
            });
        } else {
            this.models = [];
        }
    }
}
